<template>
  <loading-page v-if="loading" />
  <div
    v-else
    class="d-flex flex-column pb-2 position-relative table-wrapper"
  >
    <b-row
      v-if="!file"
      class="mb-1"
      align-content="between"
    >
      <b-col
        cols="12"
        lg="6"
      >
        <template>
          <b-button
            variant="bg-white icon-light"
            class="mr-1 mt-1 mt-sm-0"
            @click="$router.push({ name: 'buckets' })"
          >
            <GjIcon name="ArrowLeft" />
            Back
          </b-button>
          <b-button
            variant="bg-primary"
            class="mr-1"
            @click="initFileUpload"
          >
            <GjIcon
              class="icon-left"
              name="Upload"
            />
            Upload Files
          </b-button>
          <b-button
            variant="bg-white"
            class="mr-1"
            @click.stop="initNewFolder"
          >
            <GjIcon
              class="icon-left"
              name="Plus"
            />
            New Folder
          </b-button>
          <b-dropdown
            v-if="selectedRows.length > 0"
            id="object-options"
            variant="bg-white"
            class="d-none d-sm-inline-flex"
            text="Actions"
            no-caret
          >
            <template #button-content>
              Actions
              <GjIcon
                class="ml-1"
                name="ArrowDown"
              />
            </template>

            <b-dropdown-item @click="initMove(selectedRows)">
              <GjIcon name="Reply" />
              Move
            </b-dropdown-item>
            <b-dropdown-item @click="initCopy(selectedRows)">
              <GjIcon name="Copy" />
              Copy
            </b-dropdown-item>
            <b-dropdown-item @click="remove(selectedRows)">
              <GjIcon name="Delete" />
              Remove
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-col>
      <b-col
        class="justify-content-lg-end d-flex mt-lg-0 mt-1 settings-section"
        cols="12"
        lg="6"
      >
        <b-button
          variant="bg-white"
          class="mr-1"
          style="min-width: 111px;"
          @click="toggleMultiparts"
        >
          {{ showMultiparts ? 'List files' : 'List multiparts' }}
        </b-button>
        <b-button
          variant="bg-white"
          class="d-flex align-items-center flex-row-reverse"
          :to="{ name: 'bucket-settings', params: { id: bucketName, accountId } }"
        >
          <GjIcon
            class="settings_icon"
            name="Settings"
          />
          <span>Settings</span>
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <directory-content
          v-if="!showMultiparts"
          v-show="!file"
          :selected="selectedRows"
          :new-folder-row="newFolderRow"
          :parents="parentDir"
          :request-files="requestFiles"
          :active-key="activeDir"
          :key-to-focus="keyToFocus"
          :initial-fetching="initialFetching"
          :getting-file="gettingFile"
          @createFolder="createFolder"
          @cancelCreateFolder="newFolderRow = false"
          @open="open"
          @backToParent="backToParent"
          @rowSelected="arr => (selectedRows = arr)"
          @uploadFiles="initFileUpload"
          @cancelUpload="cancelUpload"
          @remove="remove"
          @loadMore="loadMore"
          @move="initMove"
          @copy="initCopy"
          @refresh="refresh"
        />
        <multiparts
          v-if="showMultiparts"
          :refresh-index="refreshIndex"
          :account-id="accountId"
        />
        <file-preview
          v-if="file"
          :file="file"
          :parents="parentDir"
          :active-key="activeDir"
          :selected-tab="filePreviewTab"
          :last-file-fetch="fileFetchTime"
          :account-id="accountId"
          @setFilePreviewTab="
            e => {
              filePreviewTab = e
            }
          "
          @refresh="refreshFile"
          @open="
            e => {
              file = null
              open(e)
            }
          "
          @backToParent="
            e => {
              file = null
              filePreviewTab = 0
              backToParent(e)
            }
          "
        />
      </b-col>
    </b-row>
    <b-modal
      v-model="uploadFilesModal"
      title="Upload Files"
      cancel-variant="outline-primary"
      ok-title="Upload Files"
      body-class="p-0 position-relative"
      :ok-disabled="numberOfUploadedFiles < 1"
      centered
      dialog-class="modal-custom"
      @hidden="() => {}"
      @ok.prevent="uploadFiles"
    >
      <upload-files
        v-model="uploadData"
        @numberOfFiles="num => (numberOfUploadedFiles = num)"
      />
    </b-modal>
    <move-object
      :objects-to-move="moveObjectItems"
      :active-key="activeDir"
      :parents="parentDir"
      :account-id="accountId"
      @close="moveObjectItems = null"
    />
    <copy-object
      :objects-to-copy="copyObjectItems"
      :active-key="activeDir"
      :parents="parentDir"
      :account-id="accountId"
      @close="moveObjectItems = null"
    />
  </div>
</template>
<script>
import StorageHelpers from '@/mixins/storage-helpers'
import { uploadAllFiles, cancelAllTokens } from '@/services/v2/upload'

const root = {
  isFolder: true,
  key: '',
}

export default {
  name: 'Directory',
  components: {
    DirectoryContent: () => import('./DirectoryContent.vue'),
    FilePreview: () => import('../file/FilePreview.vue'),
    UploadFiles: () => import('./helpers/UploadFiles.vue'),
    MoveObject: () => import('./helpers/move/MoveObject.vue'),
    CopyObject: () => import('./helpers/copy/CopyObject.vue'),
    Multiparts: () => import('./Multiparts.vue'),
  },
  mixins: [StorageHelpers],
  props: {
    query: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      initialized: false,
      maxKeys: 20,
      files: [],
      uploadData: {
        files: [],
        isPublic: false,
      },
      uploadFilesModal: false,
      searchInput: '',
      selectedRows: [],
      newFolderRow: false,
      numberOfUploadedFiles: 0,
      activeDir: null,
      keyToFocus: null,
      lastFetchedObject: null,
      initialFetching: false,
      moveObjectItems: null,
      copyObjectItems: null,
      file: null,
      gettingFile: '',
      filePreviewTab: 0,
      loading: false,
      refreshIndex: 1,
      accountId: 0,
    }
  },
  computed: {
    bucketName() {
      return this.$route.params.id
    },
    parentDir() {
      if (this.activeDir) {
        const lastChar = this.activeDir.substr(this.activeDir.length - 1) === '/'
        const dirString = lastChar
          ? this.activeDir.slice(0, -1)
          : this.activeDir
        return dirString.split('/')
      }
      return null
    },
    uploadPath() {
      if (this.activeDir) {
        if (this.activeDir.slice(-1) !== '/') {
          const tempArr = this.keyToArr(this.activeDir)
          tempArr.pop()
          if (tempArr.length) {
            return `${tempArr.join('/')}/`
          }
          return null
        }
        return this.activeDir
      }
      return null
    },
    requestFiles: {
      get() {
        return this.$store.state.uploading.requestFiles
      },
      set(val) {
        this.$store.commit('uploading/SET_REQUEST_FILES', val)
      },
    },
    showMultiparts: {
      get() {
        return this.$store.state.accountMultiparts.showMultiparts
      },
      set(val) {
        this.$store.commit('accountMultiparts/SET_SHOW_MULTIPARTS', val)
      },
    },
    index() {
      return this.$store.getters['uploading/index']
    },
    projectId() {
      return this.$store.state.app.projectId
    },
    projectName() {
      return this.$store.state.app.projectName
    },
  },
  watch: {
    activeDir(val) {
      this.updateKeyInQuery(val)
    },
    file(val) {
      if (val) {
        this.updateTabInQuery(this.filePreviewTab)
      } else {
        this.updateTabInQuery(null)
      }
    },
    filePreviewTab(val) {
      if (this.file) {
        this.updateTabInQuery(val)
      }
    },
    uploadPath(val) {
      this.$store.commit('uploading/SET_CURRENT_UPLOAD_PATH', val)
    },
  },
  created() {
    this.accountId = this.$route.params.accountId
    this.$store.commit('uploading/SET_BUCKET_NAME', this.bucketName)
    const givenKey = this.$route.query.key
    const { tab } = this.$route.query
    if (givenKey) {
      if (tab !== undefined && tab !== null && tab !== '' && tab <= 3) {
        this.loading = true
        this.filePreviewTab = parseInt(tab, 10)
        this.openFile({
          bucketName: this.bucketName,
          key: givenKey,
        })
      } else {
        this.getObject({
          isFolder: true,
          key: decodeURIComponent(givenKey),
        })
      }
      this.activeDir = givenKey
      this.initialized = true
      return
    }
    this.getObject(root, true)
    this.initialized = true
  },
  beforeDestroy() {
    this.$store.commit('accountMultiparts/SET_SHOW_MULTIPARTS', false)
    this.$store.commit('uploading/SET_CURRENT_UPLOAD_PATH', null)
    this.$store.commit('uploading/SET_BUCKET_NAME', null)
  },
  methods: {
    updateTabInQuery(val) {
      let query = `${this.$route.path}?key=${encodeURIComponent(
        this.activeDir,
      )}`
      if (val !== null) {
        const tab = this.file ? `&tab=${val}` : ''
        if (this.initialized && tab) {
          query = `${this.$route.path}?key=${encodeURIComponent(
            this.activeDir,
          )}${tab}`
        }
      }
      // eslint-disable-next-line no-restricted-globals
      history.pushState({}, null, query)
    },
    updateKeyInQuery() {
      const tab = this.file ? `&tab=${this.filePreviewTab}` : ''
      // eslint-disable-next-line no-restricted-globals
      history.pushState(
        {},
        null,
        `${this.$route.path}?key=${encodeURIComponent(this.activeDir)}${tab}`,
      )
    },
    refresh() {
      if (this.showMultiparts) {
        this.refreshIndex++
      } else {
        this.$store.dispatch('accountObjects/refreshObjects', { accountId: this.accountId })
      }
    },
    cancelUpload(index) {
      cancelAllTokens(index)
    },
    loadMore() {
      this.$store.dispatch('accountObjects/getMoreObjects', this.accountId)
    },
    backToParent(key) {
      this.open({
        bucketName: this.bucketName,
        isFolder: true,
        key: key || '',
        maxKeys: this.maxKeys,
      })
    },
    initFileUpload() {
      this.showMultiparts = false
      this.numberOfUploadedFiles = 0
      this.uploadFilesModal = true
    },
    initMove(items) {
      this.moveObjectItems = items
    },
    initCopy(items) {
      this.copyObjectItems = items
    },
    remove(items) {
      const isSingle = items.length === 1
      this.confirmDelete(
        `Delete ${
          isSingle ? this.formatObjectName(items[0].key) : 'multiple files'
        }`,
        'This action is irreversible. To confirm deletion, click the button below.',
      ).then(ok => {
        if (ok) {
          const prefixes = []
          const keys = []
          items.forEach(item => {
            if (item.isFolder) {
              prefixes.push(item.key)
            } else {
              keys.push(item.key)
            }
          })
          this.$store
            .dispatch('accountObjects/deleteObjects', {
              accountId: this.accountId,
              query: {
                bucketName: this.bucketName,
                toDelete: { keys, prefixes },
              },

            })
            .then(() => {
              this.$store.dispatch('accountObjects/refreshUntilStatusCleared', { accountId: this.accountId })
              this.successToast('Deleted successfully!')
            })
            .catch(() => {
              this.errorToast('Error deleting')
            })
        }
      })
    },
    initNewFolder() {
      this.showMultiparts = false
      this.newFolderRow = true
    },
    createFolder(name) {
      const key = `${this.activeDir ? this.activeDir : ''}${name}`
      this.$store
        .dispatch('accountObjects/addObject', {
          accountId: this.accountId,
          payload: {
            bucketName: this.bucketName,
            object: {
              isFolder: true,
              key,
            },
          },
        })
        .then(() => {
          this.newFolderRow = false
          this.setKeyToFocus(`${key}/`)
          this.$store.dispatch('accountObjects/prependObjects', [
            {
              bucketName: this.bucketName,
              isFolder: true,
              key: `${key}/`,
              manuallyAdded: true,
            },
          ])
        })
        .catch(err => {
          const msg = err.response?.data?.errors[0]
            ? err.response?.data?.errors[0]
            : 'Error creating folder'
          this.errorToast(msg)
        })
    },
    async uploadFiles() {
      const controller = new AbortController()
      this.uploadFilesModal = false

      const payload = {
        uploadData: this.uploadData,
        data: {
          accountId: this.accountId,
          bucketName: this.bucketName,
          controller,
          index: this.requestFiles.length,
          isUploading: true,
          projectId: this.projectId,
          projectName: this.projectName,
          uploadPath: this.uploadPath,
          isPublic: this.uploadData.isPublic,
        },
      }
      this.requestFiles.push(payload)
      const summary = await uploadAllFiles(payload.data.index)
      if (summary && summary.failed > 0 && !summary.cancelled) {
        const message = `${summary.failed} of ${summary.total} file(s) failed to upload.`
        if (summary.succeeded === 0) {
          this.errorToast(message, 'Upload failed')
        } else {
          this.infoToast(message, 'Some files failed')
        }
      }
    },
    getObject(object, first) {
      if (first) this.initialFetching = true

      const query = {
        bucketName: this.bucketName,
        isFolder: object.isFolder,
        key: object.key,
        maxKeys: this.maxKeys,
      }

      return this.$store
        .dispatch('accountObjects/getObject', { accountId: this.accountId, query })
        .then(() => true)
        .catch(err => {
          if (err.response?.status === 424) {
            this.$router.push({ name: 'buckets-start' })
            return false
          }
          if (err?.response?.status === 404) {
            this.errorToast('Folder not found.')
          } else {
            this.errorToast(
              err.response?.data?.errors[0] || 'An error occurred',
            )
          }
          return false
        })
        .finally(() => {
          if (first) {
            this.initialFetching = false
          }
        })
    },
    setKeyToFocus(key) {
      this.keyToFocus = key
      const v = this
      setTimeout(() => {
        v.keyToFocus = null
      }, 3000)
    },
    open(object) {
      if (object.isFolder) {
        this.getObject({ key: object.key, bucketName: this.bucketName })
          .then(
            ok => {
              if (ok) {
                this.lastFetchedObject = object
              }
              this.activeDir = object.key
            },
          )
      } else {
        this.openFile(object)
      }
    },
    openFile(object) {
      this.gettingFile = object.key
      const query = {
        accountId: this.accountId,
        ...object,
        bucketName: this.bucketName,
      }

      this.$store
        .dispatch('accountObjects/fetchObjectInfo', query)
        .then(res => {
          this.gettingFile = ''
          this.activeDir = object.key
          this.file = res.data.result
          this.fileFetchTime = new Date().toString()
        })
        .catch(error => {
          if (error.response?.status === 424) {
            this.$router.push({ name: 'buckets-start' })
            return
          }
          this.errorToast(
            error.response?.data?.errors[0] || 'Error opening file',
          )
        })
        .finally(() => {
          this.gettingFile = ''
          this.loading = false
        })
    },
    refreshFile() {
      this.openFile({ ...this.file })
    },
    toggleMultiparts() {
      this.showMultiparts = !this.showMultiparts
      if (this.showMultiparts) {
        this.selectedRows = []
      }
    },
  },
}
</script>

<style lang="scss" scoped>

.settings_icon{
  margin-left: 7px;
}

.settings-section {
  @media (max-width: 380px) {
    flex-wrap: wrap;
    a.btn {
      margin-top: 1rem;
    }
  }
}
</style>
